/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/notifications-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-notifications-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/notifications-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, organization_id, user_id, project_id, job_id, lot_id, area_id, activity_id, change_order_id, extra_work_order_id, inventory_item_id, purchase_order_id, document_id, is_read, is_delivered_in_app, is_delivered_email, is_delivered_sms
 * - Sortable fields: created_at
 * - Required fields: organization_id, user_id, notification_type_code, title, message
 * - Readonly fields: id, created_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/notifications-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-notifications-api-client.ts (API client)
 * - client/api/generated_schemas/notifications.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:43.898Z
 * Source config: config/entities/notifications-entity-config.ts
 * Public fields: 26/26 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationsClient, NotificationsQueryOptions } from '../generated_clients/generated-notifications-api-client.js';
import {
  Notifications,
  NotificationsInsert,
  NotificationsUpdate,
  NotificationsFieldConfig
} from '../generated_schemas/generated-notifications-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/notifications_business_hooks/

// Query keys with enhanced caching strategy
export const notificationsKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...notificationsKeys.lists(), { filters }] as const,
  details: () => [...notificationsKeys.all, 'detail'] as const,
  detail: (id: string) => [...notificationsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...notificationsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...notificationsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...notificationsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...notificationsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useNotificationsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new NotificationsClient();
};

/**
 * Hook to get all notifications with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with notifications data
 */
export const useNotificationsList = (options?: NotificationsQueryOptions) => {
  const client = useNotificationsClient();

  return useQuery({
    queryKey: notificationsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single notification by ID
 *
 * @param id - The notification ID
 * @returns React Query result with single notification data
 */
export const useNotifications = (id: string) => {
  const client = useNotificationsClient();

  return useQuery({
    queryKey: notificationsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/notifications
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/notifications_business_hooks/
 */

/**
 * Hook to create a new notification
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: organization_id, user_id, notification_type_code, title, message
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at
 *
 * @returns React Query mutation for creating notification
 */
export const useCreateNotifications = () => {
  const client = useNotificationsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: NotificationsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to update an existing notification
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at
 *
 * @returns React Query mutation for updating notification
 */
export const useUpdateNotifications = () => {
  const client = useNotificationsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: NotificationsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: notificationsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a notification
 *
 * @returns React Query mutation for deleting notification
 */
export const useDeleteNotifications = () => {
  const client = useNotificationsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
    },
  });
};

/**
 * Hook to count notifications with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, organization_id, user_id, project_id, job_id, lot_id, area_id, activity_id, change_order_id, extra_work_order_id, inventory_item_id, purchase_order_id, document_id, is_read, is_delivered_in_app, is_delivered_email, is_delivered_sms
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useNotificationsCount = (filter?: Partial<Record<typeof NotificationsFieldConfig.filterable[number], any>>) => {
  const client = useNotificationsClient();
  
  return useQuery({
    queryKey: notificationsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useNotificationsFieldConfig = () => {
  return NotificationsFieldConfig;
};